import { useEffect, useState, useRef, type ReactNode } from "react";
import { styled, darkTheme } from "~/ui/style/stitches.config";
import { CommunityAuthor } from "~/api/communityApi";
import { Button } from "~/ui/components/Button";
import { useSnapshot } from "valtio";
import { localState } from "~/state/state";
import { isIOSNativeApp } from "~/utils/deviceDetection";

interface ProfilePopoverProps {
  author: CommunityAuthor;
  children: ReactNode;
  position?: "right" | "below";
}

export const ProfilePopover = ({ author, children, position = "right" }: ProfilePopoverProps) => {
  const [isActive, setIsActive] = useState(false);
  const { isOnMobile } = useSnapshot(localState);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside tap (mobile)
  useEffect(() => {
    if (!isActive || !isOnMobile) return;
    const handleTap = (e: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsActive(false);
      }
    };
    document.addEventListener("pointerdown", handleTap);
    return () => document.removeEventListener("pointerdown", handleTap);
  }, [isActive, isOnMobile]);

  return (
    <StyledPopoverWrapper
      ref={containerRef}
      onPointerEnter={() => { if (!isOnMobile) setIsActive(true); }}
      onPointerLeave={() => { if (!isOnMobile) setIsActive(false); }}
      onClick={(e) => {
        if (isOnMobile) {
          e.stopPropagation();
          setIsActive(!isActive);
        }
      }}
    >
      {children}
      {isActive && (
        <div className={`popover-card ${position === "below" ? "position-below" : ""}`}>
          <div className="popover-name">{author.name}</div>
          {author.location && (
            <div className="popover-location">{author.location}</div>
          )}
          {author.bio && (
            <div className="popover-bio">{author.bio}</div>
          )}
          {!isIOSNativeApp() && (
            <Button
              lightweight
              style={{ marginTop: "10px", marginBottom: "0px", width: "100%", height: "30px" }}
              onClick={(e) => {
                e.stopPropagation();
                window.open(`/profile/${author.username}`, "_blank");
              }}
            >
              View profile
            </Button>
          )}
        </div>
      )}
    </StyledPopoverWrapper>
  );
};

const StyledPopoverWrapper = styled("div", {
  position: "relative",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "inherit",

  "& .popover-card": {
    cursor: "default",
    position: "absolute",
    left: "calc(100% + 12px)",
    top: "50%",
    transform: "translateY(-50%)",
    width: "170px",
    borderRadius: "20px",
    backgroundColor: "white",
    border: "1px solid black",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    fontSize: "12px",
    zIndex: 100,
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
    "@mobile": {
      fontSize: "13px",
    },
    "&.position-below": {
      left: 0,
      right: "auto",
      top: "calc(100% + 8px)",
      transform: "none",
      // Bridge above for below-positioned popover
      "&:before": {
        content: "",
        position: "absolute",
        bottom: "100%",
        left: 0,
        right: 0,
        height: "12px",
      },
    },
    // Invisible bridge so mouse can travel from trigger to popover (right position)
    "&:not(.position-below):before": {
      content: "",
      position: "absolute",
      right: "100%",
      top: 0,
      bottom: 0,
      width: "16px",
      "@mobile": {
        pointerEvents: "none",
      },
    },
  },

  "& .popover-name": {
    fontWeight: "600",
    overflowWrap: "break-word",
    width: "100%",
  },

  "& .popover-location": {
    color: "$gray500",
    fontSize: "10px",
    marginTop: "4px",
  },

  "& .popover-bio": {
    width: "100%",
    marginTop: "6px",
    maxHeight: "114px",
    display: "-webkit-box",
    WebkitLineClamp: 4,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    lineHeight: "1.4",
  },

  [`.${darkTheme.className} &`]: {
    "& .popover-card": {
      backgroundColor: "#000",
      borderColor: "rgba(255, 255, 255, 0.6)",
      color: "$text",
      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.4)",
      "& button": { backgroundColor: "#e5e5e5", color: "#000", "&:hover": { backgroundColor: "#d0d0d0" } },
    },
    "& .popover-location": { color: "$textMuted" },
  },
});

